import { create } from 'zustand';
import { persist, devtools, createJSONStorage } from 'zustand/middleware';

/**
 * @description: Zustand store for managing prompt limits and click counts.
 * @module usePromptLimitStore
 * 
 * This store allows you to manage the number of prompts a user can make and track how many times they have clicked.
 * It includes methods to set the prompt limit, increment the click count, reset the click count, and check if the limit has been reached.
 */
interface PromptLimitState {
    promptLimit: number;
    clickCount: number;
    setPromptLimit: (limit: number) => void;
    incrementClickCount: () => void;
    resetClickCount: () => void;
    hasReachedLimit: () => boolean;
}

/**
 * @description: Zustand store for managing prompt limits and click counts.
 * @module usePromptLimitStore
 * 
 * The default prompt limit is set to 1, and the click count starts at 0.
 * Limit of -1 indicates no limit.
 * 
 * @example
 * import usePromptLimitStore from 'store/slices/usePromptLimitStore';
 * 
 * // Usage in a component
 * const MyComponent = () => {
 *  const promptLimit = usePromptLimitStore((state) => state.promptLimit);
 *  const setPromptLimit = usePromptLimitStore((state) => state.setPromptLimit);
 *  const incrementClickCount = usePromptLimitStore((state) => state.incrementClickCount);
 *  const resetClickCount = usePromptLimitStore((state) => state.resetClickCount);
 *  const hasReachedLimit = usePromptLimitStore((state) => state.hasReachedLimit);
 * 
 *  return (
 *    <div>
 *      <p>Prompt Limit: {promptLimit}</p>
 *      <button onClick={incrementClickCount}>Increment Click Count</button>
 *      <button onClick={resetClickCount}>Reset Click Count</button>
 *      <p>Has Reached Limit: {hasReachedLimit() ? 'Yes' : 'No'}</p>
 *    </div>
 *  );
 * 
 */
const usePromptLimitStore = create<PromptLimitState>()(
    devtools(
        persist(
            (set, get) => ({
                promptLimit: 1,
                clickCount: 0,
                setPromptLimit: (limit) => set({ promptLimit: limit }),
                incrementClickCount: () => set((state) => ({ clickCount: state.clickCount + 1 })),
                resetClickCount: () => set({ clickCount: 0 }),
                hasReachedLimit: () => {
                    const { promptLimit, clickCount } = get();
                    return promptLimit !== -1 && clickCount >= promptLimit;
                },
            }),
            {
                name: 'prompt-limit-storage',
                storage: createJSONStorage(() => localStorage),
            }
        )
    )
);

export default usePromptLimitStore;
